/**
 * useReasonGate — the shared "reason for change" gate (CLAUDE.md standing rule
 * 11 / ALPACA ReasonRequired).  Combines the global audit-trail config
 * (Settings → Audit Trail Configuration) with the shared prompt dialog so NO
 * form hardcodes its own reason logic.
 *
 * Returns a function `(formName, action?) => Promise<string | null>`:
 *   • If the form has ReasonRequired ON → prompts for a reason and returns it
 *     (or null when the user cancels → the caller should abort the edit/delete).
 *   • If ReasonRequired is OFF → resolves to '' immediately (proceed, no reason).
 *
 * The returned reason should be passed to the form's existing mutation so the
 * API persists it on the audit row (IAuditTrailService.WriteAsync `Reason`).
 *
 *   const gateReason = useReasonGate();
 *   const reason = await gateReason('City', 'delete');
 *   if (reason === null) return;              // user cancelled
 *   await del.mutateAsync({ id, reason });    // reason may be ''
 */
import { useCallback } from 'react';
import { useConfirm } from './useConfirm';
import { isReasonRequired } from '../../utils/auditConfig';

export function useReasonGate() {
  const { prompt } = useConfirm();
  return useCallback(
    async (formName: string, action: 'edit' | 'delete' = 'delete'): Promise<string | null> => {
      if (!isReasonRequired(formName)) return '';
      return prompt({
        title: 'Reason for change',
        message: `Enter a reason for this ${action}:`,
        placeholder: 'Reason…',
        required: true,
      });
    },
    [prompt],
  );
}
